import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { StatCard } from '../components/StatCard'
import { Spinner } from '../../../components/ui/Spinner'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { formatDate } from '../../../lib/utils'

const useDashboardStats = () =>
  useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => {
      const [
        { count: totalUsers },
        { data: subs },
        { data: signups },
        { count: linkedPairs },
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('subscriptions').select('plan, billing_cycle, status, is_comped, created_at'),
        supabase.from('profiles')
          .select('created_at')
          .gte('created_at', new Date(Date.now() - 30 * 86400_000).toISOString())
          .order('created_at'),
        supabase.from('linked_households')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'active'),
      ])

      const rows = subs ?? []
      const paid      = rows.filter((s) => s.plan === 'paid' && s.status !== 'canceled' && !s.is_comped).length
      const free      = (totalUsers ?? 0) - paid - rows.filter((s) => s.is_comped).length
      const comped    = rows.filter((s) => s.is_comped).length
      const monthly   = rows.filter((s) => s.billing_cycle === 'monthly' && ['active', 'trialing'].includes(s.status) && !s.is_comped).length
      const annual    = rows.filter((s) => s.billing_cycle === 'annual'  && ['active', 'trialing'].includes(s.status) && !s.is_comped).length
      const mrr       = Math.round((monthly * 4 + annual * (40 / 12)) * 100) / 100

      // Build signups-per-day map for last 30 days
      const days = {}
      for (let i = 29; i >= 0; i--) {
        const d = new Date(Date.now() - i * 86400_000)
        days[d.toISOString().slice(0, 10)] = 0
      }
      for (const s of signups ?? []) {
        const d = s.created_at.slice(0, 10)
        if (d in days) days[d]++
      }
      const chartData = Object.entries(days).map(([date, count]) => ({
        date: date.slice(5),  // MM-DD
        count,
      }))

      return {
        totalUsers:  totalUsers ?? 0,
        paid,
        free:        Math.max(0, free),
        comped,
        mrr,
        newSignups:  (signups ?? []).length,
        linkedPairs: linkedPairs ?? 0,
        chartData,
      }
    },
  })

export const DashboardPage = () => {
  const { data, isLoading } = useDashboardStats()

  return (
    <div className="px-8 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">Dashboard</h1>
        <p className="text-sm text-text-muted mt-1">Platform overview</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            <StatCard title="Total Users"     value={data.totalUsers.toLocaleString()} accent="primary" />
            <StatCard title="Paid"            value={data.paid.toLocaleString()}       accent="emerald"
              subtitle={`MRR ~$${data.mrr.toFixed(2)}`} />
            <StatCard title="Free"            value={data.free.toLocaleString()}       accent="sky" />
            <StatCard title="Comped"          value={data.comped.toLocaleString()}     accent="violet" />
            <StatCard title="New (30 days)"   value={data.newSignups.toLocaleString()} accent="amber" />
            <StatCard title="Linked HH Pairs" value={data.linkedPairs.toLocaleString()} accent="primary" />
          </div>

          <div className="bg-surface rounded-base border border-border p-6">
            <h2 className="text-sm font-semibold text-text-primary mb-4">New Signups — Last 30 Days</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e4f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#7c6faf' }}
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: '#7c6faf' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: '#ffffff',
                    border: '1px solid #e8e4f0',
                    borderRadius: '12px',
                    fontSize: 12,
                  }}
                  cursor={{ fill: '#f0eaff' }}
                />
                <Bar dataKey="count" name="Signups" fill="#2d1b5e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}
